import { Button } from '@abyss/web/ui/Button';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Flex } from '@abyss/web/ui/Flex';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import { HcbsService } from '#/models/ProviderDetails';
import { find } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { capitalizeFirstLetters } from '../../../../utils/providerNames.utils';
import { InfoProviderContent } from '../InfoProviderContent';
import {
  HcbsSubHeadingListStyles,
  HcbsSubHeadingText,
} from '../ProviderLocationsDetails.style';

type Props = {
  hcbsServiceData: HcbsService;
  notAvailableOrUnknownText: string;
};

export const HcbsServiceInfo = ({
  hcbsServiceData,
  notAvailableOrUnknownText,
}: Props) => {
  const { t } = useTranslation();
  const data = useLagoon('ui-messaging')();
  const hcbsContent = find(data, {
    key: ConstantsLagoon.CONTENT_FILTERS.HCBS_TOOLTIP_INFO_ICON,
  });

  const [enableHcbsCountiesSeeMore] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_HCBS_COUNTIES_SEE_MORE,
  ]);

  const [isCountiesDrawerOpen, setIsCountiesDrawerOpen] = useState(false);
  const sortedCounties = hcbsServiceData.countyServices
    ?.slice()
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  const COUNTIES_INLINE_LIMIT = 3;
  const inlineCounties = enableHcbsCountiesSeeMore
    ? sortedCounties?.slice(0, COUNTIES_INLINE_LIMIT)
    : sortedCounties;
  const hasMoreCounties =
    enableHcbsCountiesSeeMore &&
    (sortedCounties?.length ?? 0) > COUNTIES_INLINE_LIMIT;

  return (
    <React.Fragment>
      <InfoProviderContent
        icon="home_work"
        iconType="symbol"
        infoTitle={t(
          'PROVIDER_LOCATIONS_DETAILS.HOME_AND_COMMUNITY_BASED_SERVICES.HEADING'
        )}
        isHcbs
        popoverContent={hcbsContent?.message}
        popoverTestId="hcbs-services-info-icon-popover"
        popoverTitle={hcbsContent?.title}
        showPopover
        variant="filled"
      >
        <Flex
          css={{
            gap: '8px',
          }}
          direction="column"
        >
          <Flex
            css={{ gap: '4px' }}
            data-testid="sub-heading-hcbs-services"
            direction="column"
          >
            <HcbsSubHeadingText>
              {t(
                'PROVIDER_LOCATIONS_DETAILS.HOME_AND_COMMUNITY_BASED_SERVICES.SUB_HEADING_SERVICES'
              )}
            </HcbsSubHeadingText>
            {hcbsServiceData.serviceCategory?.length > 0 ? (
              <Text>
                <HcbsSubHeadingListStyles>
                  {hcbsServiceData.serviceCategory?.map((services) => (
                    <li
                      data-testid="hcbs-service-category"
                      key={services}
                      style={{ paddingBottom: '4px' }}
                    >
                      {capitalizeFirstLetters(services)}
                    </li>
                  ))}
                </HcbsSubHeadingListStyles>
              </Text>
            ) : (
              <Text>
                <HcbsSubHeadingListStyles>
                  <li>
                    <Text> {notAvailableOrUnknownText}</Text>
                  </li>
                </HcbsSubHeadingListStyles>
              </Text>
            )}
          </Flex>
          <Flex
            css={{ gap: '4px' }}
            data-testid="sub-heading-hcbs-waivers"
            direction="column"
          >
            <HcbsSubHeadingText>
              {t(
                'PROVIDER_LOCATIONS_DETAILS.HOME_AND_COMMUNITY_BASED_SERVICES.SUB_HEADING_WAIVERS'
              )}
            </HcbsSubHeadingText>
            {hcbsServiceData.waivers?.length > 0 ? (
              <Text>
                <HcbsSubHeadingListStyles>
                  {hcbsServiceData.waivers?.map((waiver) => (
                    <li
                      data-testid="hcbs-waiver"
                      key={waiver}
                      style={{ paddingBottom: '4px' }}
                    >
                      {capitalizeFirstLetters(waiver)}
                    </li>
                  ))}
                </HcbsSubHeadingListStyles>
              </Text>
            ) : (
              <Text>
                <HcbsSubHeadingListStyles>
                  <li>
                    <Text> {notAvailableOrUnknownText}</Text>
                  </li>
                </HcbsSubHeadingListStyles>
              </Text>
            )}
          </Flex>
          <Flex
            css={{ gap: '4px' }}
            data-testid="sub-heading-hcbs-counties-serviced"
            direction="column"
          >
            <HcbsSubHeadingText>
              {t(
                'PROVIDER_LOCATIONS_DETAILS.HOME_AND_COMMUNITY_BASED_SERVICES.SUB_HEADING_COUNTIES_SERVICED'
              )}
            </HcbsSubHeadingText>
            {sortedCounties?.length > 0 ? (
              <Text>
                <HcbsSubHeadingListStyles>
                  {inlineCounties?.map((county) => (
                    <li
                      data-testid="hcbs-county-services"
                      key={county}
                      style={{ paddingBottom: '4px' }}
                    >
                      {capitalizeFirstLetters(county)}
                    </li>
                  ))}
                </HcbsSubHeadingListStyles>
                {hasMoreCounties && (
                  <Link
                    data-testid="hcbs-view-all-counties-link"
                    onClick={() => setIsCountiesDrawerOpen(true)}
                  >
                    {t(
                      'PROVIDER_LOCATIONS_DETAILS.HOME_AND_COMMUNITY_BASED_SERVICES.VIEW_ALL_COUNTIES'
                    )}
                  </Link>
                )}
              </Text>
            ) : (
              <Text>
                <HcbsSubHeadingListStyles>
                  <li>
                    <Text> {notAvailableOrUnknownText}</Text>
                  </li>
                </HcbsSubHeadingListStyles>
              </Text>
            )}
          </Flex>
        </Flex>
      </InfoProviderContent>
      <Drawer
        css={{
          'abyss-modal-header-container': {
            borderBottom: '1px solid #E5E5E6',
          },
        }}
        data-testid="hcbs-counties-serviced-drawer"
        footer={
          <Flex
            css={{
              borderTop: '1px solid #E5E5E6',
              padding: '16px',
              width: '100%',
            }}
          >
            <Button
              data-testid="hcbs-counties-drawer-close-button"
              onClick={() => setIsCountiesDrawerOpen(false)}
              variant="solid"
            >
              {t('Close')}
            </Button>
          </Flex>
        }
        isOpen={isCountiesDrawerOpen}
        onClose={() => setIsCountiesDrawerOpen(false)}
        position="right"
        size="$sm"
        title={t(
          'PROVIDER_LOCATIONS_DETAILS.HOME_AND_COMMUNITY_BASED_SERVICES.SUB_HEADING_COUNTIES_SERVICED'
        )}
      >
        <Flex
          css={{ paddingLeft: '16px', paddingTop: '20px' }}
          data-testid="hcbs-counties-drawer-container"
          direction="column"
        >
          {sortedCounties?.map((county) => (
            <Text
              css={{ paddingBottom: '4px' }}
              data-testid="hcbs-county-services-drawer-item"
              key={county}
            >
              {capitalizeFirstLetters(county)}
            </Text>
          ))}
        </Flex>
      </Drawer>
    </React.Fragment>
  );
};
